import React, {
    type ChangeEventHandler, useCallback,
    useState,
} from 'react';

import debounce from 'lodash.debounce';
import {searchProductResources} from 'src/openapi/admin/apis/ProductEntitlementsResourceSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';


import {MAX_RESULTS, UNCLEAR_QUERY_MESSAGE} from './constants';
import {ResourceSuggestResult} from './resource-suggest-result';

import type {ResourceSearchItem} from 'src/openapi/admin/models/ResourceSearchItem';
import type {ServerResponse} from 'src/typedef';

type Props = {
    id: string,
    accountId?: number,
    userId?: number,
    onSearch: (resourceIds: string[] | null, query: string) => void,
};

export const ProductResourceSuggest: React.FC<Props> = ({id, onSearch, accountId, userId}) => {
    const apiDispatch = useAppDispatch();
    const {createAbortSignal, abort} = useAbortSignal();
    const [shown, setShown] = useState(false);

    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState<ServerResponse<ResourceSearchItem[]> | null>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getSuggestions = useCallback(debounce(async (newValue: string) => {
        const signal = createAbortSignal();
        const resourceName = newValue.toLowerCase().trim();
        if (!resourceName) {
            setBusy(false);
            setResponse(null);
            abort();
            return;
        }

        if (resourceName.length < 3) {
            setResponse({error: {message: UNCLEAR_QUERY_MESSAGE}});
            setBusy(false);
            abort();
            return;
        }

        const searchResult = await apiDispatch(searchProductResources(
            {resourceName, accountId, userId},
            {signal},
        ));
        if (searchResult.payload.success) {
            setResponse({data: searchResult.payload.data.slice(0, MAX_RESULTS)});
        } else {
            setResponse({error: {
                message: 'Can\'t suggest resources. Service is temporary unavailable.',
            }});
        }
        setBusy(false);
    }, 1000), [
        createAbortSignal, accountId, userId, setResponse, setBusy, abort,
    ]);

    const onChangeQuery = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;
        setBusy(true);
        setQuery(value);
        getSuggestions(value);
    }, [setBusy, setQuery, getSuggestions]);

    const clearSuggest = useCallback(() => {
        setQuery('');
        setResponse(null);
        onSearch(null, '');
    }, [setQuery, setResponse, onSearch]);

    const onItemSelect = useCallback((resource: ResourceSearchItem, reason: string) => {
        setQuery(resource.name);
        setResponse({data: [resource]});

        if (reason === 'MOUSE_DOWN') {
            onSearch([resource.id], resource.name);
        }
    }, [setResponse, setQuery, onSearch]);

    const onKeyUp = useCallback<React.KeyboardEventHandler<HTMLDivElement>>(event => {
        if (event.key === 'Enter') {
            event.currentTarget.blur();
        }
    }, []);

    const onBlur = useCallback(() => {
        setShown(false);
        onSearch(response?.data?.map(resource => resource.id) || null, query);
    }, [response, query, onSearch, setShown]);

    return (
        <div style={{width: 500}}>
            <Autosuggest
                id={id}
                shown={shown}
                setShown={setShown}
                value={query}
                maxLength={64}
                placeholder="Search for resources by keyword"

                onKeyUp={onKeyUp}
                onChange={onChangeQuery}
                onBlur={onBlur}
                onItemSelect={onItemSelect}
                onClear={clearSuggest}>
                <ResourceSuggestResult
                    id={`${id}-result`}
                    busy={busy}
                    response={response}
                />
            </Autosuggest>
        </div>
    );
};
